import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, ArrowUpRight, Send, X, MessageSquare, Settings as Cog, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/** Persistent dashboard CEO card (Phase J.5).
 *
 * Spec contract:
 *   1. Always renders for active users — never hidden by missing data.
 *   2. Greeting: "Welcome back, {name} from {agency_name}. How can I help you today?"
 *      Falls back to "Welcome back, {name}. How can I help you today?" when
 *      `agency_name` is missing.
 *   3. If identity is NOT approved AND user hasn't dismissed onboarding →
 *      shows the onboarding hint with three buttons:
 *        "Complete Agency Setup" · "Chat with CEO" · "Skip for now"
 *   4. Once skipped (localStorage flag) → soft reminder line replaces the hint.
 *   5. Chat button opens an in-place CEO chat drawer that calls the new
 *      `/api/ceo/dashboard-chat` endpoint.
 */
const SKIP_KEY = "aivos_agency_onboarding_skipped";

export default function DashboardCeoCard({ stats }) {
    const { user } = useAuth();
    const [agency, setAgency] = useState(null);
    const [chatOpen, setChatOpen] = useState(false);
    const [skipped, setSkipped] = useState(() => localStorage.getItem(SKIP_KEY) === "1");
    const nav = useNavigate();

    useEffect(() => {
        api.get("/agency").then(({ data }) => setAgency(data)).catch(() => {});
    }, []);

    const name = (user?.name || user?.email?.split("@")[0] || "there").trim();
    const agencyName = (agency?.agency_name || "").trim();
    const identityStatus = (agency?.identity_status || "draft").toLowerCase();
    const isApproved = identityStatus === "approved";
    const showOnboardingHint = !!agency && !isApproved && !skipped;
    const showSoftReminder = !!agency && !isApproved && skipped;

    const oneliner = ceoOneliner(stats, isApproved);
    const tips = useMemo(() => buildTips(stats), [stats]);

    const handleSkip = () => {
        localStorage.setItem(SKIP_KEY, "1");
        setSkipped(true);
    };

    return (
        <div data-testid="dashboard-ceo-card" className="card-base p-6 ceo-glow relative">
            {/* Greeting */}
            <div className="flex items-start gap-4">
                <div className="shrink-0 w-10 h-10 rounded-sm border border-[#FF4400]/40 bg-[#FF4400]/10 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-[#FF4400]" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="label-micro text-[#FF4400] mb-1">CEO Agent</div>
                    <div className="text-sm text-white" data-testid="dashboard-ceo-greeting">
                        Welcome back, <span className="font-medium">{name}</span>
                        {agencyName ? (
                            <>
                                <span className="text-zinc-500"> from </span>
                                <span className="font-medium">{agencyName}</span>
                            </>
                        ) : null}
                        <span className="text-zinc-500">. How can I help you today?</span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">{oneliner}</div>
                </div>
            </div>

            {/* Onboarding hint (only when identity not approved AND not skipped) */}
            {showOnboardingHint && (
                <div
                    data-testid="dashboard-ceo-onboarding-hint"
                    className="mt-4 p-3 border border-amber-500/30 bg-amber-500/5 rounded-sm"
                >
                    <div className="flex items-start gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-zinc-200 leading-relaxed">
                            Before we begin, I'd love to understand your agency better so I can
                            work like your personalized creative CEO. Share your company name,
                            website, social links, services, logo, and preferred style — this
                            sharpens every strategy, brief, and branded output.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                        <Link
                            to="/settings"
                            data-testid="dashboard-ceo-cta-setup"
                            className="px-3 py-1.5 text-xs bg-[#FF4400] hover:bg-[#ff5a1f] text-white rounded-sm inline-flex items-center gap-1.5 font-medium"
                        >
                            <Cog className="w-3.5 h-3.5" /> Complete Agency Setup
                        </Link>
                        <button
                            type="button"
                            data-testid="dashboard-ceo-cta-chat"
                            onClick={() => setChatOpen(true)}
                            className="px-3 py-1.5 text-xs border border-zinc-800 bg-zinc-950 hover:bg-zinc-900 rounded-sm inline-flex items-center gap-1.5"
                        >
                            <MessageSquare className="w-3.5 h-3.5" /> Chat with CEO
                        </button>
                        <button
                            type="button"
                            data-testid="dashboard-ceo-cta-skip"
                            onClick={handleSkip}
                            className="px-3 py-1.5 text-xs text-zinc-500 hover:text-zinc-200"
                        >
                            Skip for now
                        </button>
                    </div>
                </div>
            )}

            {/* Soft reminder (only after skip) */}
            {showSoftReminder && (
                <div
                    data-testid="dashboard-ceo-soft-reminder"
                    className="mt-4 p-2.5 border border-zinc-900 bg-zinc-950 rounded-sm flex items-center justify-between gap-2"
                >
                    <p className="text-[11px] text-zinc-400 leading-snug flex items-start gap-1.5">
                        <span className="w-1 h-1 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                        Your agency profile is still incomplete. Completing it helps me
                        personalize your campaigns, reports, and strategy better.
                    </p>
                    <Link
                        to="/settings"
                        data-testid="dashboard-ceo-soft-reminder-cta"
                        className="text-[11px] font-mono uppercase tracking-wider text-[#FF4400] hover:text-[#ff5a1f] shrink-0"
                    >
                        Complete →
                    </Link>
                </div>
            )}

            {/* Always-on action row: Chat + tips */}
            <div className="mt-4 flex flex-wrap gap-2">
                <button
                    type="button"
                    data-testid="dashboard-ceo-chat-btn"
                    onClick={() => setChatOpen(true)}
                    className="px-3 py-2 text-xs bg-white text-black hover:bg-zinc-200 rounded-sm inline-flex items-center gap-1.5 font-medium"
                >
                    <MessageSquare className="w-3.5 h-3.5" /> Chat with CEO
                </button>
                {tips.slice(0, 1).map((t, i) => (
                    <button
                        key={i}
                        type="button"
                        data-testid="dashboard-ceo-next-action"
                        onClick={() => nav(t.to)}
                        className="px-3 py-2 text-xs border border-zinc-800 bg-zinc-950 hover:bg-zinc-900 rounded-sm inline-flex items-center gap-1.5 text-zinc-200"
                    >
                        <span className="w-1 h-1 rounded-full bg-[#FF4400]" /> {t.label}
                        <ArrowUpRight className="w-3 h-3 text-zinc-500" />
                    </button>
                ))}
            </div>

            {tips.length > 1 && (
                <div className="mt-3 space-y-1.5">
                    {tips.slice(1, 3).map((t, i) => (
                        <button
                            key={i}
                            type="button"
                            data-testid={`dashboard-ceo-tip-${i}`}
                            onClick={() => nav(t.to)}
                            className="w-full text-left px-3 py-2 rounded-sm border border-zinc-900 hover:border-[#FF4400]/40 hover:bg-zinc-950 transition-colors flex items-center justify-between gap-3 group"
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                <span className="w-1 h-1 rounded-full bg-[#FF4400]" />
                                <span className="text-sm text-zinc-200 truncate">{t.label}</span>
                            </div>
                            <ArrowUpRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-[#FF4400]" />
                        </button>
                    ))}
                </div>
            )}

            {chatOpen && (
                <DashboardCeoChat
                    onClose={() => setChatOpen(false)}
                    agencyName={agencyName}
                    userName={name}
                    onCompleteSetup={() => { setChatOpen(false); nav("/settings"); }}
                />
            )}
        </div>
    );
}

/* ----------------------- Inline chat drawer ----------------------- */

function DashboardCeoChat({ onClose, agencyName, userName, onCompleteSetup }) {
    const greeting = agencyName
        ? `Welcome back, ${userName} from ${agencyName}. How can I help you today?`
        : `Welcome back, ${userName}. How can I help you today?`;
    const [messages, setMessages] = useState([{ role: "agent", text: greeting }]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);

    const send = async () => {
        const t = (text || "").trim();
        if (!t || busy) return;
        const next = [...messages, { role: "user", text: t }];
        setMessages(next);
        setText("");
        setBusy(true);
        try {
            const { data } = await api.post("/ceo/dashboard-chat", {
                text: t,
                history: next.slice(0, -1).slice(-12),
            });
            setMessages([...next, { role: "agent", text: data.reply || "(no reply)" }]);
        } catch (e) {
            setMessages([...next, {
                role: "agent",
                text: `(CEO temporarily unreachable — ${e.userMessage || e.message}. Please retry.)`,
            }]);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="dashboard-ceo-chat-modal">
            <div className="w-full sm:max-w-2xl sm:mx-auto h-[80vh] sm:h-[70vh] bg-[#0a0a0a] border border-zinc-800 rounded-t-lg sm:rounded-sm flex flex-col">
                <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-900">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-[#FF4400]" />
                        <div className="label-micro text-[#FF4400]">CEO Agent</div>
                        {agencyName && <span className="text-xs text-zinc-500">· {agencyName}</span>}
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="dashboard-ceo-chat-close"
                        className="text-zinc-500 hover:text-white"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto scrollbar-thin px-5 py-4 space-y-3">
                    {messages.map((m, i) => (
                        <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[85%] p-3 rounded-sm text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-white text-black" : "bg-[#141414] border border-zinc-800 text-zinc-100"}`}>
                                {m.text}
                            </div>
                        </div>
                    ))}
                    {busy && <div className="text-zinc-500 text-xs font-mono uppercase tracking-wider">CEO is thinking…</div>}
                </div>
                <div className="border-t border-zinc-900 p-3 flex flex-wrap gap-2 items-center">
                    <button
                        type="button"
                        onClick={onCompleteSetup}
                        data-testid="dashboard-ceo-chat-setup"
                        className="px-2.5 py-1.5 text-[11px] border border-zinc-800 rounded-sm bg-zinc-950 hover:bg-zinc-900 text-zinc-300 inline-flex items-center gap-1"
                    >
                        <Cog className="w-3 h-3" /> Agency Setup
                    </button>
                    <input
                        data-testid="dashboard-ceo-chat-input"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                        placeholder="Ask the CEO Agent…"
                        className="flex-1 min-w-[180px] bg-black border border-zinc-800 rounded-sm px-3 py-2 text-sm focus:border-zinc-500 outline-none"
                    />
                    <button
                        data-testid="dashboard-ceo-chat-send"
                        onClick={send}
                        disabled={busy || !text.trim()}
                        className="btn-primary px-4 py-2 text-sm disabled:opacity-50"
                    >
                        <Send className="w-4 h-4" /> Send
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ----------------------- Helpers ----------------------- */

function ceoOneliner(stats, isApproved) {
    if (!stats) return isApproved ? "Loading studio overview…" : "Tell me what you want to ship today.";
    const { total_clients = 0, active_projects = 0, pending_approvals = 0 } = stats;
    if (total_clients === 0) return "No clients yet — add one to start your first campaign.";
    if (active_projects === 0) return "All projects shipped. Time to start the next one.";
    if (pending_approvals > 0) {
        return `${pending_approvals} stage${pending_approvals === 1 ? "" : "s"} awaiting your approval.`;
    }
    return `${active_projects} active project${active_projects === 1 ? "" : "s"} — nothing blocked, momentum is good.`;
}

function buildTips(stats) {
    if (!stats) return [
        { label: "Brief a new campaign", to: "/projects/new" },
        { label: "Add your first client", to: "/clients/new" },
    ];
    const out = [];
    const { total_clients = 0, active_projects = 0, pending_approvals = 0, recent_projects = [] } = stats;
    if (pending_approvals > 0) {
        out.push({ label: `Approve ${pending_approvals} pending stage${pending_approvals === 1 ? "" : "s"}`, to: "/projects" });
    }
    if (total_clients === 0) {
        out.push({ label: "Add your first client", to: "/clients/new" });
    }
    if (active_projects === 0 && total_clients > 0) {
        out.push({ label: "Brief a new campaign", to: "/projects/new" });
    }
    if (recent_projects.length > 0) {
        const p = recent_projects[0];
        out.push({ label: `Resume "${p.title}"`, to: `/projects/${p.id}` });
    }
    if (out.length < 3) {
        out.push({ label: "Brief a new campaign", to: "/projects/new" });
    }
    return out.slice(0, 3);
}
